import { useRequest } from 'ahooks';
import { Col, Row } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { MeasureType } from 'domains/measurementDomain';
import { SkuPresetSize, SkuPresetSizes } from 'domains/skuDomain';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import './SkuPresetSizePicker.less';

interface Props {
  /**
   * The names currently selected, for the unit being shown.
   *
   * Optional because antd injects this - and `onChange` - into the child of a named `Form.Item`.
   * A caller that passes them explicitly would have them overwritten.
   */
  value?: string[];
  /** Called with the new selection for the unit being shown. */
  onChange?: (names: string[]) => void;
  disabled?: boolean;
}

/**
 * The ready-made finished sizes a SKU offers, as cards the supplier toggles on and off.
 *
 * A selection, not a shortcut: picking A4 does not put numbers in the size table, it records that
 * this SKU offers A4. The two live side by side - a SKU can offer ready-made sizes, hand-typed
 * ones, or both - and a buyer is shown the two merged.
 *
 * Metric and imperial are two catalogues rather than one converted list, because the names are
 * not interchangeable: a metric SKU offers A4, not a converted Letter.
 *
 * Which of the two a SKU fills in follows the unit the supplier is working in, which is their own
 * profile setting where they have one. A SKU can therefore end up offering both, which is the
 * point of keeping two lists. The buyer's size step is fetched with the reader's unit too, so
 * the list that gets filled is the list that gets read.
 */
export const SkuPresetSizePicker = ({ value, onChange, disabled }: Props) => {
  const { t } = useTranslation();
  // The unit this person is working in - their own profile choice where they have one, the org's
  // default otherwise. It picks the catalogue they see and the one they fill, and the buyer's
  // size step is fetched with the same answer, so the two cannot disagree.
  const { metric, abbrev } = useMeasurementUnit();
  const unitLabel = abbrev(MeasureType.LENGTH);

  const { data, loading, error } = useRequest<SkuPresetSizes, []>(
    skuService.getPresetSizes,
    {
      cacheKey: 'sku-preset-sizes',
      // An enhancement, not a requirement: a size step that cannot reach the catalogue still
      // takes hand-typed sizes, so this must never surface as an error on the page.
      onError: (e) =>
        // eslint-disable-next-line no-console
        console.warn('[SkuPresetSizePicker] could not load the preset sizes', e),
    }
  );

  const presets: SkuPresetSize[] = useMemo(
    () => (metric ? data?.metric : data?.imperial) ?? [],
    [data, metric]
  );

  const selected = useMemo(() => new Set(value ?? []), [value]);
  // Collapsed to start: the catalogue runs to sixty-odd sizes, and unfolding all of them above
  // the size table would bury it. The count in the heading says what is selected without opening.
  const [expanded, setExpanded] = useState(false);

  if (loading || error) {
    return null;
  }

  if (!presets.length) {
    // Nothing to show is a configuration state, not a bug, so it stays out of the supplier's way.
    // It is said out loud here because an unseeded dictionary otherwise looks exactly like a
    // feature that was never built.
    // eslint-disable-next-line no-console
    console.warn(
      `[SkuPresetSizePicker] no ${
        metric ? 'metric' : 'imperial'
      } preset sizes are configured - seed sys_dict_data from ` +
        'db/manual/PRD-498_preset_sizes.sql'
    );
    return null;
  }

  const toggle = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    // Kept in catalogue order rather than click order, so the stored list reads the same way the
    // cards do and two suppliers who picked the same sizes store the same thing.
    onChange?.(presets.map((p) => p.name).filter((n) => next.has(n)));
  };

  return (
    <Row className="sku-preset-size-picker">
      {/* Same label column as the Sizes table above, so the two line up. */}
      <Col span={6} className="ant-form-item-label">
        <label
          className={`sku-preset-size-picker-heading${
            expanded ? ' sku-preset-size-picker-heading-open' : ''
          }`}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded((open) => !open);
            }
          }}
        >
          <RightOutlined />
          <span>{t('platform.sku.wizard.size.presetTitle')}</span>
          {selected.size > 0 && (
            <span className="sku-preset-size-picker-count">
              ({selected.size})
            </span>
          )}
        </label>
      </Col>
      <Col span={18}>
        {expanded && (
          <div className="sku-preset-size-picker-cards">
            {presets.map((preset: SkuPresetSize) => {
              const isSelected = selected.has(preset.name);
              // Drawn to the preset's own proportions, so a landscape size reads as landscape.
              // The longer side always fills the box; the shorter one is scaled against it.
              const longest = Math.max(preset.width, preset.length);
              return (
                <button
                  type="button"
                  key={preset.name}
                  aria-pressed={isSelected}
                  className={`sku-preset-size-card${
                    isSelected ? ' sku-preset-size-card-selected' : ''
                  }`}
                  disabled={disabled}
                  title={preset.description || preset.name}
                  onClick={() => toggle(preset.name)}
                >
                  {preset.image ? (
                    <img
                      className="sku-preset-size-card-image"
                      src={preset.image}
                      alt=""
                    />
                  ) : (
                    <span
                      className="sku-preset-size-card-shape"
                      style={{
                        width: `${(preset.width / longest) * 52}px`,
                        height: `${(preset.length / longest) * 52}px`,
                      }}
                    />
                  )}
                  <span className="sku-preset-size-card-name">
                    {preset.name}
                  </span>
                  <span className="sku-preset-size-card-dims">
                    {`${preset.width} × ${preset.length} ${unitLabel}`}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Col>
    </Row>
  );
};
